import { ArraySchema, type ISchema, Lazy, ObjectSchema, Reference } from 'yup';

export interface ISchemaNode {
  schema: ISchema<any>;
  path: undefined | string;
  key?: string | number;
  /**
   * Indica se si trova all'interno di un array, e qual 'è il path di questo
   */
  inArrayPath?: string;
  inArrayIndex?: number;
  /**
   * Lista di tutti i path degli array genitori
   */
  inArrayParents?: string[];
  value: any;
  parent?: any;
  from?: any[];
}

export interface IForEachSchemaOptions {
  value: any;
  context?: any;
  moduleRequestId?: string;
  /**
   * Permette di passare una lista di nodi già calcolati, in modo da evitare
   * di dover rieseguire la traversata dello schema.
   * Utile in combinazione con `describeSchema` per evitare di dover rieseguire
   * il calcolo della descrizione dello schema.
   */
  nodes?: ISchemaNode[];
}

/**
 * Versione ottimizzata di `traverseSchema` che non applica il `describe` per
 * ogni nodo che visita, riducendo il costo computazionale.
 * Inoltre utilizza una coda FIFO per evitare di ricorrere alla ricorsione
 * e alle operazioni low-level di iterazione (`for`, `for in`)
 */
// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: critical hot path, complessità intrinseca alla traversata dello schema
export function forEachSchemaNode /* NOSONAR */(
  initialSchema: ISchema<any>,
  options: IForEachSchemaOptions,
  // biome-ignore lint/suspicious/noConfusingVoidType: <tdb>
  cb: (node: ISchemaNode) => boolean | null | undefined | void,
) {
  // Se sono stati passati i nodi già calcolati, li utilizziamo direttamente
  // senza dover rieseguire la traversata dello schema.
  if (options.nodes?.length) {
    for (const node of options.nodes) {
      const shouldBreakEarly = cb(node);
      if (shouldBreakEarly === false) return;
    }
    return;
  }

  const queue: ISchemaNode[] = [
    { schema: initialSchema, path: undefined, value: options.value },
  ];

  // Indice di lettura al posto di queue.shift() che è O(n) su array.
  // Gli slot già processati vengono nullificati per permettere al GC di
  // liberare i nodi non più necessari.
  let headIdx = 0;
  while (headIdx < queue.length) {
    const node = queue[headIdx];
    queue[headIdx] = null as any;
    headIdx++;
    if (!node) continue;
    const schema = node.schema.resolve({ ...node, context: options.context });
    const shouldBreakEarly = cb({ ...node, schema });
    if (shouldBreakEarly === false) return;

    if (schema instanceof ArraySchema) {
      if (!(Array.isArray(node.value) && schema.innerType)) {
        continue;
      }

      const parentsList = node.inArrayParents ?? [];
      const valuesLen = node.value.length;
      for (let i = 0; i < valuesLen; i++) {
        const value = node.value[i];
        const path = node.path ? `${node.path}.${i}` : `${i}`;
        queue.push({
          schema: schema.innerType,
          key: i,
          inArrayPath: node.path,
          inArrayIndex: i,
          inArrayParents: node.path ? [...parentsList, path] : parentsList,
          path,
          value,
          // È necessario che il parent non sia l'array ma l'oggetto padre, per
          // via di come gestiamo il `parentWhen`: `^.a` deve fare riferimento
          // all'oggetto padre e non all'array in se.
          //
          // Se volessimo cambiare questo comportamento, dobbiamo predisporre o
          // una sintassi diversa, ma sicuramente dovremmo allineare anche
          // `yup` per gestire correttamente la risoluzione del `from`.
          from: node.from ? [...node.from] : [],
        });
      }
    }

    if (schema instanceof ObjectSchema) {
      const from = [{ schema, value: node.value }, ...(node.from ?? [])];

      for (const key in schema.fields) {
        if (Reference.isRef(schema.fields[key])) continue;

        const value = node.value?.[key];
        const path = node.path ? `${node.path}.${key}` : `${key}`;
        queue.push({
          schema: schema.fields[key],
          inArrayPath: node.inArrayPath,
          inArrayIndex: node.inArrayIndex,
          inArrayParents: node.inArrayParents,
          key,
          path,
          value,
          parent: node.value,
          from,
        });
      }
    }

    if (schema instanceof Lazy) {
      queue.push({ ...node, schema: schema.resolve(options) });
    }
  }
}
